import { Document, StyleSheet, Image, Link, View, Page, Text, Font } from '@react-pdf/renderer';
import PropTypes from 'prop-types';
import React from 'react';

import CircularStdMedium from '../../../../../fonts/CircularStd-Medium.otf';
import CircularStdLight from '../../../../../fonts/CircularStd-Light.otf';
import Quicksand from '../../../../../fonts/quicksand-v9-latin-700.woff';
import { ControlPropType } from '../../../form_fields/ctrl_proptype';

Font.register({
  family: 'Circular Std Medium',
  src: CircularStdMedium,
});

Font.register({
  family: 'Circular Std Light',
  src: CircularStdLight,
});

Font.register({
  family: 'Quicksand',
  src: Quicksand,
});

const styles = StyleSheet.create({
  response: {
    fontFamily: 'Circular Std Medium',
    color: 'black',
    fontSize: 14,
    width: '70%'
  },
  subTitle: {
    fontFamily: 'Quicksand',
    fontSize: 15.75,
    fontWeight: 600,
  },
  divider: {
    borderBottom: '1px solid #7d7c7c',
    fontFamily: 'Circular Std Light',
    paddingBottom: 4,
    marginBottom: 10,
    paddingTop: 45,
    color: 'black',
    fontSize: 20,
  },
  product: {
    title: {
      borderBottom: '1px solid #7d7c7c',
      paddingVertical: 8
    },
    total: {
      marginTop: 8
    },
    image: {
      backgroundColor: '#f1f1f1',
      borderRadius: 8,
      height: '65px',
      width: '65px',
    },
    item: {
      borderBottom: '1px solid #7d7c7c',
      justifyContent: 'flex-start',
      flexDirection: 'row',
      paddingVertical: 8,
      display: 'flex',
      width: '100%',
      gap: 8
    }
  },
  content: {
    flexDirection: 'column',
    paddingHorizontal: 48,
    marginBottom: 42,
    display: 'flex',
    paddingTop: 42,
    gap: 18
  },
  header: {
    justifyContent: 'space-between',
    backgroundColor: '#0494b6',
    paddingHorizontal: 20,
    flexDirection: 'row',
    paddingVertical: 20,
    display: 'flex',
    width: '100vw',
  },
  label: {
    fontFamily: 'Circular Std Medium',
    color: 'black',
    fontSize: 16,
    width: '30%'
  },
  table: {
    rowHeader: {
      borderBottom: '1px solid #7d7c7c',
      paddingBottom: 5,
    },
    rowLabel: {
      marginBottom: 5
    },
    column: {
      // justifyContent: 'flex-start',
      // flexDirection: 'row',
      paddingVertical: 5,
      // display: 'flex',
      width: '100%',
      // gap: 8
    },
    row: {
      marginBottom: 18
    }
  },
  title: {
    fontFamily: 'Circular Std Medium',
    textAlign: 'right',
    color: 'white',
    width: 'auto',
    fontSize: 20,
  },
  brand: {
    flexDirection: 'row',
    alignItems: 'center',
    color: '#a9e5f3',
    display: 'flex',
    fontSize: 14,
    gap: 12
  },
  entry: {
    flexDirection: 'row',
    display: 'flex',
    gap: 20
  },
  body: {
    fontSize: 16,
  },
  logo: {
    width: 80
  },
  link: {
    fontFamily: 'Circular Std Medium',
    textDecoration: 'none',
    color: '#0494b6',
    fontSize: 14,
    width: '70%'
  }
});

// Lib
const hyperTextFieldTypes = ['file_upload', 'embed_field', 'website'];

const supportedNonValuedFieldTypes = [
  'media_header',
  'description',
  'media_image',
  'embed_field'
];

const structuredFieldTypes = [
  'inventory_field',
  'select_multiple',
  'matrix_rating',
  'table_field',
  'checkboxes',
  'signature',
  'ranking',
  'address'
];

// Utils
const getHyperTextValue = (type, value) => type === 'file_upload' ? value.path : value;

const filterProps = ({ props, filters }) => Object.fromEntries(
	Object.entries(props).filter(([key, _]) => filters.includes(key))
);

const getSupportedNonValuedFieldData = (type, props) => {
  if (!supportedNonValuedFieldTypes.includes(type)) return null;
  const supportedNonValuedFieldData = {
    media_image: {
      value: props.image_url,
      label: 'Embed Image'
    },
    media_header: {
      label: 'Section Header',
      value: props.text
    },
    description: {
      label: 'Description',
      value: props.text
    },
    embed_field: {
      label: 'Embed Video',
      valueProp: props.url
    }
  };

  return supportedNonValuedFieldData[type];
};

const formatDate = (timestamp) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const today = new Date();
  
  const parsedDate = new Date(timestamp || today); // Handle NaN error
  let day = parsedDate.getDate();
  const lastDigitOfDay = day[-1];
  const daySuffixes = {
    ist: lastDigitOfDay === 1,
    nd: lastDigitOfDay === 2,
    rd: lastDigitOfDay === 3,
    th: true
  };

  const date = () => {
    const daySuffix = Object.entries(daySuffixes).find(([_,v]) => v)[0];
    const month = months[parsedDate.getMonth()];
    const year = parsedDate.getFullYear();
    const newDay = `${day}${daySuffix}`;
    return `${newDay} ${month}, ${year}`;
  };

  const time = () => {
    const hour = String(parsedDate.getHours() % 12 || 12).padStart(2, '0');
    const minute = String(parsedDate.getMinutes()).padStart(2, '0');
    const ampm = parsedDate.getHours() >= 12 ? 'pm' : 'am';
    return `${hour}:${minute}${ampm}`;
  };

  return { data: date(), time: time()};
};

// Commponents
const Response = ({
  isDirectResponse = false,
  structuredFieldProps,
  value,
  type
}) => {
  const StructuredResponse = structuredFieldTypes.includes(type) && structuredFields[type].component;
  const hyperTextFieldValue = hyperTextFieldTypes.includes(type) && getHyperTextValue(type, value);
  
  return structuredFieldTypes.includes(type) ? (
    <StructuredResponse
      isDirectResponse={isDirectResponse}
      {
        ...filterProps({
          filters: structuredFields[type]?.props ?? ['value'],
          props: structuredFieldProps
        }
      )}
    />
  ) : hyperTextFieldTypes.includes(type) ? (
    <Link style={styles.link} src={hyperTextFieldValue}>
      {/* The split method helps with overflow */}
      {type === 'file_upload' && 'Path: '}
      {hyperTextFieldValue.split('')}
    </Link>
  ) : type === 'media_image' ? (
    <Image
      style={isDirectResponse ? styles.response : {}}
      src={value}
    />
  ) : (
    <Text style={isDirectResponse ? styles.response : {}}>
      {value}
    </Text>
  )
};

const TableResponse = ({ columnHeaders, columnTypes, value }) => (
  <View style={styles.response}>
    {value.map((row, rowIndex) => (
      <View style={styles.table.row} key={rowIndex}>
        <Text style={styles.table.rowHeader}>Row {rowIndex + 1}</Text>

        {!!row.length && row.map((colEntry, colIndex) => {
          const responseProps = { value: colEntry };
          const label = columnHeaders[colIndex];
          const type = columnTypes[colIndex];

          return (
            <View style={styles.table.column} key={colIndex}>
              <Text>{label}:{' '}</Text>

              <Response
                structuredFieldProps={responseProps}
                value={colEntry}
                type={type}
              />
            </View>
          );
        })}
      </View>
    ))}
  </View>
);

const SimpleListResponse = ({ isDirectResponse, value }) => (
  <View style={isDirectResponse ? styles.response : {}}>
    <Text>{value.join(', ')}</Text>
  </View>
);

const CheckboxResponse = ({ isDirectResponse, value }) => {
  const selections = Object.entries(value).map(([k, v]) => v).filter((entry) => entry);
  return (
    <View style={isDirectResponse ? styles.response : {}}>
      <Text>{selections.join(', ')}</Text>
    </View>
  );
};

const MatrixResponse = ({ isDirectResponse, value }) => (
  <View style={isDirectResponse ? styles.response : {}}>
    {value.map((entry) => (
      entry && (
        <Text key={entry}>
          {entry}
        </Text>)
    ))}
  </View>
);

const AuditTrailDate = ({ timeStamp }) => {
  const { data, time } = formatDate(timeStamp);
  return (
    <View style={styles.entry}>
      <Text style={styles.label}>Date: </Text>
      <View style={styles.response}>
        <Text>{data}</Text>
        <Text>{time}</Text>
      </View>
    </View>
  );
};

const InventoryResponse = ({ value }) => {
  const { transaction_id, products } = value;
  const productPrices = products.map(({ price, selection }) => price * selection.quantity);
  const totalPrice = productPrices.reduce((a, b) => a + b, 0);
  const { payment_currency } = products[0];
  
  const getProductCombination = (combination) => {
    if (Object.keys(combination).length) {
      const options = Object.entries(combination);
      return options.map((option) => (
        option.join(': ')
      )).join(', ');
    }
    return null;
  };

  return (
    <View style={styles.response}>
      <Text>
        Payment Status: {!transaction_id && 'NOT'} PAID
      </Text>

      <Text style={styles.product.title}>Products</Text>
      {products.map(({ selection, product_title, image, price, id }) => {
        const combination = getProductCombination(selection.combination);

        return (
          <View style={styles.product.item} key={id}>
            <Image style={styles.product.image} src={image} />
            <View>
              <Text>{product_title}</Text>
              <Text>Quantity: {selection.quantity}</Text>
              {combination && (<Text>{combination}</Text>)}
              <Text>Price: {price} {payment_currency}</Text>
            </View>
          </View>
        )
      })}
      <Text style={styles.product.total}>
        Total: {totalPrice} {payment_currency}
      </Text>
    </View>
  );
};

const AddressResponse = ({ value }) => (
  <View style={styles.response}>
    {Object.entries(value).map(([_, entry]) => (
      entry && (<Text key={entry}>{entry}</Text>)
    ))}
  </View>
);

const Header = ({ title }) => (
  <View style={styles.header}>
    <View style={styles.brand}>
      <Image src="/static/images/logo/Small-White.png" style={styles.logo} />
      <Text style={styles.subTitle}>
        E-Sign
      </Text>
    </View>

    {title && (
      <Text style={styles.title}>
        {title}
      </Text>
    )}
  </View>
);

export const structuredFields = {
  inventory_field: {
    component: InventoryResponse
  },
  select_multiple: {
    component: SimpleListResponse
  },
  matrix_rating: {
    component: MatrixResponse
  },
  checkboxes: {
    component: CheckboxResponse
  },
  table_field: {
    props: ['columnHeaders', 'columnTypes', 'value'],
    component: TableResponse
  },
  signature: {
    component: Image,
    props: ['src']
  },
  address: {
    component: AddressResponse
  },
  ranking: {
    component: SimpleListResponse
  }
};

const ESignPDFTemplate = ({ form, onRender }) => (
  <Document style={{ width: '100vw' }} title={form.name} author="Formplus" creator="Formplus E-Sign" producer="Formplus E-Sign">
    {form.definition.pages.map(({ controls }, pageIndex) => {
      const pageHasFieldsWithValues = controls.some(({ value }) => value);
      return pageHasFieldsWithValues && (
        <Page style={styles.body} size="A4" key={pageIndex}>
          {pageIndex === 0 && (
            <>
              {/* USED FOR RETRIEVING DOCUMENT DATA ON RENDER */}
              <Text render={onRender} />
              <Header />
            </>
          )}

          <View style={styles.content}>
            {pageIndex === 0 && (
              <View style={styles.entry}>
                <Text style={styles.label}>Title: </Text>
                <Text style={styles.response}>{form.name}</Text>
              </View>
            )}
            
            {controls.map(({ internal_id, dataURL = null, type, value, props }) => {
              const nonValuedFieldData = getSupportedNonValuedFieldData(type, props);
              const isValueToDisplay = value ? value : nonValuedFieldData?.value;
              const response = type !== 'add_row' && isValueToDisplay && {
                label: value ? props.label : nonValuedFieldData?.label,
                type: type !== 'lookup' ? type : props.field_type,
                value: isValueToDisplay,
                structuredFieldProps: {
                  columnHeaders: props.column_headers,
                  columnTypes: props.column_types,
                  src: dataURL,
                  value
                },
              };

              return response && (
                <View key={internal_id} style={styles.entry}>
                  {/* LABEL */}
                  <Text style={styles.label}>
                    {response.label}: 
                  </Text>

                  {/* RESPONSE */}
                  <Response
                    structuredFieldProps={response.structuredFieldProps}
                    value={response.value}
                    type={response.type}
                    isDirectResponse
                  />
                </View>
              )
            })}
          </View>
        </Page>
      )
    })}

    {form?.auditTrail && (
      <Page style={styles.body} size="A4">
        <Header title="Audit Trail" />

        <View style={styles.content}>
          <View style={styles.entry}>
            <Text style={styles.label}>Title: </Text>
            <Text style={styles.response}>
              {form.name}
            </Text>
          </View>

          <View style={styles.entry}>
            <Text style={styles.label}>Form Owner: </Text>
            <Text style={styles.response}>
              {form.auditTrail.form_owner_name}
            </Text>
          </View>

          <View style={styles.entry}>
            <Text style={styles.label}>Form Owner's email': </Text>
            <Text style={styles.response}>
              {form.auditTrail.form_owner_email}
            </Text>
          </View>

          <View style={styles.entry}>
            <Text style={styles.label}>Document ID: </Text>
            <Text style={styles.response}>
              {form.auditTrail.documentId}
            </Text>
          </View>
          
          <View style={styles.entry}>
            <Text style={styles.label}>Document Pages: </Text>
            <Text render={({ totalPages }) => totalPages} style={styles.response} />
          </View>

          <View style={styles.entry}>
            <Text style={styles.label}>Time Zone: </Text>
            <Text style={styles.response}>
              {form.auditTrail.timezone}
            </Text>
          </View>
          
          <Text style={styles.divider}>
            SIGNED BY
          </Text>
          
          <View style={styles.entry}>
            <Text style={styles.label}>Name: </Text>
            <Text style={styles.response}>
              {form.auditTrail.name}
            </Text>
          </View>

          <View style={styles.entry}>
            <Text style={styles.label}>Email: </Text>
            <Text style={styles.response}>
              {form.auditTrail.email}
            </Text>
          </View>

          <View style={styles.entry}>
            <Text style={styles.label}>IP Address: </Text>
            <Text style={styles.response}>
              {form.auditTrail.ipAddress}
            </Text>
          </View>

          <AuditTrailDate timeStamp={form.auditTrail.timeStamp} />
        </View>
      </Page>
    )}
  </Document>
);

ESignPDFTemplate.propTypes = {
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  onRender: PropTypes.func.isRequired
};

const ResponseValuePropTypes = {
  SimpleListResponse: PropTypes.arrayOf(PropTypes.string).isRequired,
  CheckboxResponse: PropTypes.objectOf(PropTypes.string).isRequired,
  AddressResponse: PropTypes.objectOf(PropTypes.string).isRequired,
  MatrixResponse: PropTypes.arrayOf(PropTypes.string).isRequired,
  TableResponse: PropTypes.arrayOf(PropTypes.array).isRequired,
  InventoryResponse: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.object),
    transaction_id: PropTypes.string
  }).isRequired
};

const ResponsePropTypes = {
  value: PropTypes.oneOfType(Object.values(ResponseValuePropTypes)),
  isDirectResponse: PropTypes.bool
};

SimpleListResponse.propTypes = ResponsePropTypes;
CheckboxResponse.propTypes = ResponsePropTypes;
MatrixResponse.propTypes = ResponsePropTypes;

InventoryResponse.propTypes = {
  value: ResponseValuePropTypes.InventoryResponse
};

AddressResponse.propTypes = {
  value: ResponseValuePropTypes.AddressResponse
};

AuditTrailDate.propTypes = {
  timeStamp: PropTypes.string.isRequired
};

TableResponse.propTypes = {
  columnHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  columnTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: ResponseValuePropTypes.TableResponse
};

Response.propTypes = {
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.array]),
  type: PropTypes.string.isRequired,
  isDirectResponse: PropTypes.bool,
  structuredFieldProps: PropTypes.shape({
    value: PropTypes.oneOfType([...Object.values(ResponseValuePropTypes), PropTypes.string]),
    columnHeaders: PropTypes.arrayOf(PropTypes.string),
    columnTypes: PropTypes.arrayOf(PropTypes.string),
    src: PropTypes.string
  })
};

Header.propTypes = {
  title: PropTypes.string
};

export { ESignPDFTemplate };